import { PageHeader, PageShell } from '@/components/PageShell';
import { DocumentStatusBadge } from '@/components/badges/DocumentStatusBadge';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { type SpaceFilter, STATUS_FILTER_LABELS, type StatusFilter } from '@/components/filters/documentStatus';
import { useDocumentListFilters } from '@/components/filters/useDocumentListFilters';
import { useRowClick } from '@/lib/navigation';
import { lessonQueryFlagsState, lessonTypesState, spacesState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { DocumentStatusSchema, type LessonType } from '@rock/types';
import { orderBy } from 'lodash';
import { type FC, useMemo } from 'react';
import { QueryStatus } from '../considerations/applies-to/QueryStatus';
import type { QueryFlag } from '../considerations/appliesToStatus';

export const LessonTypesPage: FC = () => {
    // `useAsyncAtomValue`, not a bare read: these are async atoms under the layout's shared
    // Suspense, where a suspend unmounts the subtree and the atom refetches on remount.
    const lessonTypes = useAsyncAtomValue(lessonTypesState, []);
    const spaces = useAsyncAtomValue(spacesState, []);
    const queryFlags = useAsyncAtomValue(lessonQueryFlagsState);
    const onRowClick = useRowClick();
    const { statusFilter, spaceFilter, setStatusFilter, setSpaceFilter } = useDocumentListFilters();

    const sortedSpaces = useMemo(() => orderBy(spaces, (s) => s.name.toLowerCase()), [spaces]);

    const sortedTypes = useMemo(() => {
        const filtered = lessonTypes.filter((type) => {
            if (statusFilter !== 'all' && type.documentStatus !== statusFilter) return false;
            if (spaceFilter !== 'all' && type.spaceId !== spaceFilter) return false;
            return true;
        });
        return orderBy(filtered, [(t) => t.title.toLowerCase(), (t) => t.spaceId?.toLowerCase() ?? '']);
    }, [lessonTypes, statusFilter, spaceFilter]);

    return (
        <PageShell>
            <PageHeader
                title="Lessons"
                description="Knowledge the app gives a homeowner to read. Unlike a consideration, a lesson has no tasks and nothing to accept."
            >
                <Select value={spaceFilter} onValueChange={(value) => setSpaceFilter(value as SpaceFilter)}>
                    <SelectTrigger className="w-[180px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Spaces</SelectItem>
                        {sortedSpaces.map((space) => (
                            <SelectItem key={space.id} value={space.id}>
                                {space.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as StatusFilter)}>
                    <SelectTrigger className="w-[160px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {DocumentStatusSchema.options.map((status) => (
                            <SelectItem key={status} value={status}>
                                {STATUS_FILTER_LABELS[status]}
                            </SelectItem>
                        ))}
                        <SelectItem value="all">{STATUS_FILTER_LABELS.all}</SelectItem>
                    </SelectContent>
                </Select>
            </PageHeader>

            {sortedTypes.length === 0 ? (
                <Card>
                    <CardHeader>
                        <CardTitle>No lessons found</CardTitle>
                        <CardDescription>
                            {statusFilter === 'all' && spaceFilter === 'all'
                                ? 'Lessons appear here once they are synced from the Notion Lesson Documents database.'
                                : 'No lessons match the current filters. Try a different filter.'}
                        </CardDescription>
                    </CardHeader>
                </Card>
            ) : (
                <Card>
                    <CardHeader>
                        <CardTitle>Lessons</CardTitle>
                        <CardDescription>Tap a row to see the content and which homes received it.</CardDescription>
                    </CardHeader>
                    <CardContent className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="min-w-[240px]">Title</TableHead>
                                    <TableHead className="min-w-[140px]">Space</TableHead>
                                    <TableHead className="min-w-[110px]">Status</TableHead>
                                    <TableHead className="min-w-[160px]">Delivery</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sortedTypes.map((type) => (
                                    <TableRow
                                        key={type.id}
                                        className="cursor-pointer"
                                        onClick={onRowClick(`/content/lessons/${encodeURIComponent(type.id)}`)}
                                    >
                                        <TableCell className="font-medium">{type.title}</TableCell>
                                        <TableCell>{type.spaceId ?? '-'}</TableCell>
                                        <TableCell>
                                            <DocumentStatusBadge status={type.documentStatus} />
                                        </TableCell>
                                        <TableCell>
                                            <DeliveryBadge type={type} flags={queryFlags?.[type.id]} />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}
        </PageShell>
    );
};

/**
 * How far this lesson actually gets, which is not obvious from any other column.
 *
 * Three gates decide it, and each has a counter-intuitive failure mode worth naming here:
 * - its approved query, exactly as a consideration's; until one is approved (or if it no longer
 *   validates) the lesson reaches nobody, which the flags say.
 * - `optional` → created for homes and queued like any other, but last out of the queue, so on a busy
 *   home it may only ever appear to someone browsing the library.
 * - a `spaceId` → only reaches homes that have finished setting that space up.
 */
const DeliveryBadge: FC<{ type: LessonType; flags: QueryFlag[] | undefined }> = ({ type, flags }) => {
    if (!flags) return '…';
    if (type.defaultCriticality === 'optional' && flags.length === 0) {
        return (
            <Badge variant="outline" className="text-muted-foreground border-muted-foreground bg-background">
                library only
            </Badge>
        );
    }
    return <QueryStatus query={type.appliesTo} flags={flags} />;
};
